import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gauge, Settings2, TrendingDown, TrendingUp } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  CLAIM_STATUS_LABEL,
  SALES_GOALS,
  SALES_REP_ROLE_LABEL,
  SALES_VERTICALS,
  SUBJECT_KIND_LABEL,
  VERTICAL_LABEL,
  formatGhs,
  type PerformancePeriod,
  type SalesClaim,
  type SalesGoal,
  type SalesVertical,
  type SubjectKind,
} from '@/client/types/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { Panel, Segmented, compactGhs, initials, pct, selectCls, timeLeftLabel } from './components/sales-ui';
import { cn } from '@/lib/utils';
import { setCurrentRep, useSalesActing } from './sales-store';
import {
  useSalesClaimsQuery,
  useSalesDashboardQuery,
  useSalesOffersQuery,
  useSalesOutreachQuery,
  useSalesPolicyQuery,
  useSalesPurposesQuery,
  useSalesRepsQuery,
  useSalesSubjectsQuery,
} from '@/client/api/sales/sales';
import { DEFAULT_SALES_POLICY } from '@/client/types/sales';
import { displayClaimStatus } from './sales-live';
import {
  COMPARE_PERIODS,
  COMPARE_PERIOD_LABEL,
  DEFAULT_DASH_CONFIG,
  KPI_META,
  WIDGET_META,
  breakdownRows,
  buildDashInsights,
  compareCaption,
  deltaPct,
  diagnose,
  liveValueGhs,
  loadDashConfig,
  quotaCaption,
  saveDashConfig,
  type ComparePeriod,
  type DashConfig,
  type DashGoalFilter,
  type DashGroupBy,
  type DashScope,
  type KpiId,
  type OutcomeId,
  type OutcomeInsight,
  type WidgetId,
} from './dashboard-model';

const INK = '#0f172a';
const EMERALD = '#059669';

const OUTCOME_GOAL_LABEL: Record<SalesGoal, string> = {
  signup: 'Users',
  supply: 'Listers',
  monetize: 'Advertisers',
};

const OUTCOME_COLOR: Record<OutcomeId, string> = {
  users: '#0f172a',
  listers: '#059669',
  paying: '#d97706',
  advertisers: '#7c3aed',
};

type WorkKind = 'all' | 'overdue' | 'due' | 'expiring' | 'idle';

type WorkRow = {
  id: string;
  kind: Exclude<WorkKind, 'all'>;
  subjectId: string;
  title: string;
  detail: string;
  when: string;
  ghs: number;
  late?: boolean;
};

function prospectHref(id: string): string {
  return `/admin/sales/prospects/${id}`;
}

function formatKpi(id: KpiId, value: number | null): string {
  const meta = KPI_META.find((k) => k.id === id);
  if (value == null || !meta) return '—';
  if (meta.kind === 'ghs') return formatGhs(value);
  if (meta.kind === 'pct') return pct(value);
  if (meta.kind === 'days') return `${value.toFixed(1)}d`;
  return String(Math.round(value));
}

function Delta({ curr, prev, invert, versus }: { curr: number | null; prev: number | null; invert?: boolean; versus?: string }) {
  const d = deltaPct(curr, prev);
  if (d == null) return null;
  const up = d >= 0;
  const good = invert ? !up : up;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium', good ? 'text-emerald-700' : 'text-rose-700')}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}
      {Math.round(d * 100)}% {versus ?? 'vs prior'}
    </span>
  );
}

function RateDelta({ curr, prev, versus }: { curr: number | null; prev: number | null; versus: string }) {
  if (curr == null || prev == null) return null;
  const pp = Math.round((curr - prev) * 100);
  if (pp === 0) {
    return <span className="text-xs text-gray-400">Flat {versus}</span>;
  }
  const up = pp > 0;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium', up ? 'text-emerald-700' : 'text-rose-700')}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}
      {pp}pp {versus}
    </span>
  );
}

function RateSpark({ values, color }: { values: (number | null)[]; color: string }) {
  const known = values.map((v, i) => (v == null ? null : { i, v })).filter((p): p is { i: number; v: number } => p != null);
  if (known.length < 2) return null;
  const w = 96;
  const h = 28;
  const max = Math.max(0.01, ...known.map((p) => p.v));
  const last = values.length - 1 || 1;
  const d = known
    .map((p, idx) => {
      const x = (p.i / last) * w;
      const y = h - (p.v / max) * (h - 4) - 2;
      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mt-2 overflow-visible" aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function OutcomeCard({
  row,
  active,
  versus,
  onClick,
}: {
  row: OutcomeInsight;
  active: boolean;
  versus: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-2xl border bg-white p-4 text-left transition-colors hover:border-gray-300',
        active ? 'border-slate-900 ring-1 ring-slate-900' : 'border-gray-200',
      )}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{row.label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-gray-900">
        {row.rate == null ? '—' : pct(row.rate)}
      </p>
      <p className="mt-1 text-sm text-gray-600">
        {row.won} won
        {row.closed > 0 ? <span className="text-gray-400"> · {row.closed} closed</span> : null}
      </p>
      {versus ? <RateDelta curr={row.rate} prev={row.prevRate} versus={versus} /> : null}
      <RateSpark values={row.history} color={OUTCOME_COLOR[row.id]} />
      <p className="mt-3 text-xs text-gray-400">
        {row.stock} in pool · {row.inPlay} in play
      </p>
      <p className="mt-0.5 text-xs text-gray-400">{row.hint}</p>
    </button>
  );
}

function RateTip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { name: string; value: number | null; color: string }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="mb-1 font-medium text-gray-900">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="tabular-nums text-gray-600">
          {p.name}: {p.value == null ? '—' : pct(p.value)}
        </p>
      ))}
    </div>
  );
}

function ChartTip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="mb-1 font-medium text-gray-900">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="tabular-nums text-gray-600">
          {p.name}: {compactGhs(p.value)}
        </p>
      ))}
    </div>
  );
}

function Meter({
  label,
  value,
  max,
  hint,
  warn,
}: {
  label: string;
  value: number;
  max: number;
  hint?: string;
  warn?: boolean;
}) {
  const r = max > 0 ? value / max : 0;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="tabular-nums text-gray-700">
          {compactGhs(value)} / {compactGhs(max)}
          <span className="ml-1.5 text-gray-400">{pct(r)}</span>
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={cn('h-full rounded-full', warn ? 'bg-amber-500' : 'bg-slate-900')}
          style={{ width: `${Math.min(100, r * 100)}%` }}
        />
      </div>
      {hint ? <p className="mt-1 text-[11px] text-gray-400">{hint}</p> : null}
    </div>
  );
}

function ClaimRow({
  claim,
  now,
  onOpen,
  extra,
  subjectName,
  purposeName,
  purposeGhs,
}: {
  claim: SalesClaim;
  now: number;
  onOpen: (id: string) => void;
  extra?: string;
  subjectName?: string;
  purposeName?: string;
  purposeGhs?: number;
}) {
  const late = new Date(claim.expiresAt).getTime() <= now;
  return (
    <button
      type="button"
      onClick={() => onOpen(claim.subjectId)}
      className="flex w-full items-center gap-3 py-2 text-left hover:bg-gray-50"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-900">{subjectName}</div>
        <div className="truncate text-xs text-gray-500">
          {purposeName}
          {extra ? ` · ${extra}` : ''}
        </div>
      </div>
      <span className={cn('text-xs font-medium', late ? 'text-rose-700' : 'text-gray-400')}>
        {timeLeftLabel(claim.expiresAt, now)}
      </span>
      <span className="w-20 text-right text-sm tabular-nums text-gray-900">{compactGhs(purposeGhs ?? 0)}</span>
    </button>
  );
}

const KIND_LABEL: Record<Exclude<WorkKind, 'all'>, string> = {
  overdue: 'Overdue',
  due: 'Follow-up',
  expiring: 'Expiring',
  idle: 'Idle',
};

const KIND_CLASS: Record<Exclude<WorkKind, 'all'>, string> = {
  overdue: 'bg-rose-50 text-rose-800',
  due: 'bg-amber-50 text-amber-900',
  expiring: 'bg-orange-50 text-orange-900',
  idle: 'bg-gray-100 text-gray-700',
};

export default function SalesDashboardPage() {
  const [scope, setScope] = useState<DashScope>('floor');
  const [period, setPeriod] = useState<PerformancePeriod>('30d');
  const [vertical, setVertical] = useState<SalesVertical | 'all'>('all');
  const [goal, setGoal] = useState<DashGoalFilter>('all');
  const [kind, setKind] = useState<SubjectKind | 'all'>('all');
  const [config, setConfig] = useState<DashConfig>(DEFAULT_DASH_CONFIG);
  const [customize, setCustomize] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [workKind, setWorkKind] = useState<WorkKind>('all');

  const { rep, canActAsAnyone } = useSalesActing();
  const navigate = useNavigate();
  const claimsQ = useSalesClaimsQuery({ limit: 200 });
  const subjectsQ = useSalesSubjectsQuery({ limit: 200 });
  const outreachQ = useSalesOutreachQuery({ limit: 200 });
  const purposesQ = useSalesPurposesQuery();
  const offersQ = useSalesOffersQuery();
  const policyQ = useSalesPolicyQuery();
  const repsQ = useSalesRepsQuery();
  const dashQ = useSalesDashboardQuery({
    period,
    compare: config.comparePeriod === 'off' ? 'off' : 'prior',
    scope: scope === 'me' ? 'me' : 'floor',
  });
  const claims = claimsQ.data?.claims ?? [];
  const subjects = subjectsQ.data?.subjects ?? [];
  const outreaches = outreachQ.data?.outreach ?? [];
  const purposes = purposesQ.data ?? [];
  const offers = offersQ.data ?? [];
  const policy = policyQ.data ?? DEFAULT_SALES_POLICY;
  const reps = repsQ.data ?? [];
  const purposeById = (id: string) => purposes.find((p) => p.id === id);
  const offerById = (id: string | undefined) => (id ? offers.find((o) => o.id === id) : undefined);
  const subjectById = (id: string) => subjects.find((s) => s.id === id);
  const getRep = (id: string) => reps.find((r) => r.id === id);
  const now = Date.now();
  const scopeRepId = scope === 'me' ? rep?.id : undefined;

  useEffect(() => {
    setConfig(loadDashConfig());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated) saveDashConfig(config);
  }, [config, hydrated]);

  useEffect(() => {
    if (!rep && scope === 'me') setScope('floor');
  }, [rep, scope]);

  const listInsights = useMemo(
    () =>
      buildDashInsights({
        subjects,
        claims,
        outreaches,
        purposes,
        offers,
        policy,
        reps,
        filters: { period, comparePeriod: config.comparePeriod, repId: scopeRepId, vertical, goal, kind },
        now,
        purposeById,
        offerById,
      }),
    [subjects, claims, outreaches, purposes, offers, policy, reps, period, config.comparePeriod, scopeRepId, vertical, goal, kind, now],
  );

  const insights = useMemo(() => {
    const dash = dashQ.data;
    if (!dash) return listInsights;
    const cur = dash.current;
    const prev = dash.prior;
    const kpis = { ...listInsights.kpis };
    const set = (id: KpiId, value: number | null | undefined, prevVal?: number | null) => {
      if (value == null) return;
      kpis[id] = { id, value, prev: prev ? (prevVal ?? null) : null };
    };
    set('claimed', cur.claimed, prev?.claimed);
    set('won', cur.won, prev?.won);
    set('lost', cur.lost, prev?.lost);
    set('expired', cur.expired, prev?.expired);
    set('wonGhs', cur.wonGhs, prev?.wonGhs);
    set('pipelineGhs', cur.pipelineGhs, prev?.pipelineGhs);
    set('winRate', cur.winRate, prev?.winRate);
    set('avgDaysToWin', cur.avgDaysToWin, prev?.avgDaysToWin);
    set('outreachSent', cur.outreachSent, prev?.outreachSent);
    if (cur.slaHitRate != null) set('slaHitRate', cur.slaHitRate, prev?.slaHitRate);
    if (cur.offerCostGhs != null) set('offerCost', cur.offerCostGhs, prev?.offerCostGhs);
    if (cur.poolAvailable != null) set('poolAvailable', cur.poolAvailable, prev?.poolAvailable);
    const quotaGhs = cur.quotaGhs ?? listInsights.quotaGhs;
    const wonGhs = cur.wonGhs ?? listInsights.wonGhs;
    return {
      ...listInsights,
      kpis,
      wonGhs,
      quotaGhs,
      remainingGhs: Math.max(0, quotaGhs - wonGhs),
    };
  }, [listInsights, dashQ.data]);

  const queue = useMemo(() => {
    if (scope !== 'me' || !rep) return [];
    return claims
      .filter((c) => c.ownerId === rep.id && ['claimed', 'in_pursuit', 'committed'].includes(c.status))
      .sort((a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime());
  }, [scope, rep, claims]);

  const call = useMemo(() => diagnose(insights), [insights]);
  const breakdown = breakdownRows(insights, config.groupBy);
  const show = (id: WidgetId) => config.widgets[id] !== false;
  const open = (subjectId: string) => navigate(prospectHref(subjectId));
  const patch = (partial: Partial<DashConfig>) => setConfig((c) => ({ ...c, ...partial }));

  const work = useMemo(() => {
    const seen = new Set<string>();
    const rows: WorkRow[] = [];
    const push = (row: WorkRow) => {
      if (seen.has(row.subjectId)) return;
      seen.add(row.subjectId);
      rows.push(row);
    };
    for (const c of insights.overdue) {
      const purpose = purposeById(c.purposeId);
      push({
        id: `od-${c.id}`,
        kind: 'overdue',
        subjectId: c.subjectId,
        title: subjectById(c.subjectId)?.displayName ?? c.subjectId,
        detail: `${purpose?.name ?? ''} · ${getRep(c.ownerId)?.name ?? ''}`.trim(),
        when: 'Overdue',
        ghs: purpose?.estimatedValueGhs ?? 0,
        late: true,
      });
    }
    for (const o of insights.dueOutreach) {
      push({
        id: `due-${o.id}`,
        kind: 'due',
        subjectId: o.subjectId,
        title: subjectById(o.subjectId)?.displayName ?? o.subjectId,
        detail: o.body.slice(0, 80) || o.channel,
        when: o.nextStepDue
          ? new Date(o.nextStepDue).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : '',
        ghs: liveValueGhs(o.subjectId, claims, purposeById),
      });
    }
    for (const c of insights.expiringSoon) {
      const purpose = purposeById(c.purposeId);
      push({
        id: `ex-${c.id}`,
        kind: 'expiring',
        subjectId: c.subjectId,
        title: subjectById(c.subjectId)?.displayName ?? c.subjectId,
        detail: `${purpose?.name ?? ''} · ${getRep(c.ownerId)?.name ?? ''}`.trim(),
        when: timeLeftLabel(c.expiresAt, now),
        ghs: purpose?.estimatedValueGhs ?? 0,
      });
    }
    for (const c of insights.idle) {
      const purpose = purposeById(c.purposeId);
      push({
        id: `id-${c.id}`,
        kind: 'idle',
        subjectId: c.subjectId,
        title: subjectById(c.subjectId)?.displayName ?? c.subjectId,
        detail: `${purpose?.name ?? ''} · ${getRep(c.ownerId)?.name ?? ''}`.trim(),
        when: timeLeftLabel(c.expiresAt, now),
        ghs: purpose?.estimatedValueGhs ?? 0,
      });
    }
    return rows;
  }, [insights, claims, now]);

  const filteredWork = workKind === 'all' ? work : work.filter((r) => r.kind === workKind);
  const workIds = useMemo(() => new Set(work.map((r) => r.subjectId)), [work]);
  const extraQueue = queue.filter((c) => !workIds.has(c.subjectId));

  const toggleKpi = (id: KpiId) => {
    setConfig((c) => {
      if (c.kpis.includes(id)) return { ...c, kpis: c.kpis.filter((k) => k !== id) };
      if (c.kpis.length >= 8) return c;
      return { ...c, kpis: [...c.kpis, id] };
    });
  };

  const invertDelta = (id: KpiId) =>
    id === 'lost' || id === 'expired' || id === 'overdue' || id === 'offerCost';

  const goKpi = (id: KpiId) => {
    if (id === 'overdue') setWorkKind('overdue');
    else if (id === 'pipelineGhs' || id === 'capacity' || id === 'pipelineCoverage') navigate('/admin/sales/pipeline');
    else if (id === 'customers' || id === 'poolAvailable') navigate('/admin/sales/prospects');
    else if (id === 'outreachSent' || id === 'replyRate' || id === 'ctr') navigate('/admin/sales/outreach');
  };

  const agentRows = insights.agents.filter((a) => a.role !== 'manager');
  const funnelMax = Math.max(1, ...insights.funnel.map((s) => s.n));
  const pipeline = insights.kpis.pipelineGhs.value ?? 0;

  return (
    <SalesPageShell
      title="Dashboard"
      description="Whether prospects become users, listers, paying customers, and advertisers — then who to touch next."
      crumbs={[]}
      icon={Gauge}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {rep && (
            <Segmented
              value={scope}
              onChange={setScope}
              options={[
                { id: 'floor', label: 'Floor' },
                { id: 'me', label: 'Me' },
              ]}
            />
          )}
          <select className={selectCls} value={period} onChange={(e) => setPeriod(e.target.value as PerformancePeriod)}>
            <option value="7d">7 days</option>
            <option value="30d">30 days</option>
            <option value="90d">90 days</option>
            <option value="all">All</option>
          </select>
          <button
            type="button"
            onClick={() => setCustomize((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium',
              customize ? 'border-slate-900 bg-slate-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
            )}
          >
            <Settings2 className="h-3.5 w-3.5" />
            Customize
          </button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className={selectCls} value={vertical} onChange={(e) => setVertical(e.target.value as SalesVertical | 'all')}>
          <option value="all">All desks</option>
          {SALES_VERTICALS.map((v) => (
            <option key={v} value={v}>
              {VERTICAL_LABEL[v]}
            </option>
          ))}
        </select>
        <select className={selectCls} value={goal} onChange={(e) => setGoal(e.target.value as DashGoalFilter)}>
          <option value="all">All outcomes</option>
          {SALES_GOALS.map((g) => (
            <option key={g} value={g}>
              {OUTCOME_GOAL_LABEL[g]}
            </option>
          ))}
          <option value="paying">Paying</option>
        </select>
        <select className={selectCls} value={kind} onChange={(e) => setKind(e.target.value as SubjectKind | 'all')}>
          <option value="all">People & orgs</option>
          {(Object.keys(SUBJECT_KIND_LABEL) as SubjectKind[]).map((k) => (
            <option key={k} value={k}>
              {SUBJECT_KIND_LABEL[k]}
            </option>
          ))}
        </select>
        {(vertical !== 'all' || goal !== 'all' || kind !== 'all') && (
          <button
            type="button"
            className="text-xs font-medium text-gray-500 hover:text-gray-800"
            onClick={() => {
              setVertical('all');
              setGoal('all');
              setKind('all');
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <section className="mb-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Prospect conversion</h2>
            <p className="text-xs text-gray-400">Close rate this window · click a card to scope the rest of the board</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Compare
            <select
              className={selectCls}
              value={config.comparePeriod}
              onChange={(e) => patch({ comparePeriod: e.target.value as ComparePeriod })}
            >
              {COMPARE_PERIODS.map((id) => (
                <option key={id} value={id}>
                  {COMPARE_PERIOD_LABEL[id]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {insights.outcomes.map((row) => (
            <OutcomeCard
              key={row.id}
              row={row}
              active={row.id === 'paying' ? goal === 'paying' : row.goal != null && goal === row.goal}
              versus={compareCaption(config.comparePeriod)}
              onClick={() => {
                if (row.id === 'paying') {
                  setGoal(goal === 'paying' ? 'all' : 'paying');
                  return;
                }
                if (!row.goal) return;
                setGoal(goal === row.goal ? 'all' : row.goal);
              }}
            />
          ))}
        </div>
        {insights.outcomeSeries.length > 1 && (
          <div className="mt-3 rounded-2xl border border-gray-200 bg-white px-3 py-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Close rate over time</p>
              <div className="flex flex-wrap gap-3">
                {insights.outcomes.map((row) => (
                  <span key={row.id} className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: OUTCOME_COLOR[row.id] }} />
                    {row.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={insights.outcomeSeries} margin={{ left: 8, right: 12, top: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} interval="preserveStartEnd" />
                  <YAxis
                    domain={[0, 1]}
                    width={36}
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
                  />
                  <Tooltip content={<RateTip />} />
                  {insights.outcomes.map((row) => {
                    const dim = goal !== 'all' && row.goal != null && row.goal !== goal;
                    return (
                      <Line
                        key={row.id}
                        type="monotone"
                        dataKey={row.id}
                        name={row.label}
                        stroke={OUTCOME_COLOR[row.id]}
                        strokeWidth={dim ? 1 : 2}
                        strokeOpacity={dim ? 0.25 : 1}
                        dot={{ r: 3, strokeWidth: 0 }}
                        activeDot={{ r: 4 }}
                        connectNulls
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      {customize && (
        <section className="mb-4 rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900">Layout</h2>
            <button type="button" className="text-xs font-medium text-gray-500 hover:text-gray-800" onClick={() => setConfig(DEFAULT_DASH_CONFIG)}>
              Reset
            </button>
          </div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">KPIs · up to 8 · click a tile to jump</p>
          <div className="mb-5 flex flex-wrap gap-1.5">
            {KPI_META.map((k) => {
              const on = config.kpis.includes(k.id);
              return (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => toggleKpi(k.id)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium',
                    on ? 'border-slate-900 bg-slate-900 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                  )}
                >
                  {k.label}
                </button>
              );
            })}
          </div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">Panels</p>
          <div className="mb-5 flex flex-wrap gap-1.5">
            {WIDGET_META.map((w) => {
              const on = config.widgets[w.id] !== false;
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setConfig((c) => ({ ...c, widgets: { ...c.widgets, [w.id]: !on } }))}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium',
                    on ? 'border-slate-900 bg-slate-900 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                  )}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section
        className={cn(
          'mb-4 rounded-2xl border border-gray-200 border-l-4 bg-white p-6',
          call.tone === 'bad' && 'border-l-rose-600',
          call.tone === 'warn' && 'border-l-amber-500',
          call.tone === 'ok' && 'border-l-emerald-600',
        )}
      >
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">This window</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-gray-900">{call.headline}</h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-600">{call.detail}</p>
            <div className="mt-5 space-y-3">
              <Meter label="Won vs quota" value={insights.wonGhs} max={insights.quotaGhs} hint={quotaCaption(period)} warn={insights.quotaGhs > 0 && insights.wonGhs / insights.quotaGhs < 0.5} />
              <Meter
                label="In play vs remaining"
                value={pipeline}
                max={Math.max(insights.remainingGhs, 1)}
                hint={insights.remainingGhs === 0 ? 'quota already cleared' : 'share of remaining quota covered by live claims'}
                warn={insights.coverage < 0.4}
              />
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
            {config.kpis.map((id) => {
              const meta = KPI_META.find((k) => k.id === id);
              const row = insights.kpis[id];
              if (!meta || !row) return null;
              return (
                <button key={id} type="button" onClick={() => goKpi(id)} className="text-left">
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{meta.label}</dt>
                  <dd className="mt-1 text-xl font-semibold tabular-nums tracking-tight text-gray-900">{formatKpi(id, row.value)}</dd>
                  {config.comparePeriod !== 'off' ? (
                    <Delta curr={row.value} prev={row.prev} invert={invertDelta(id)} versus={compareCaption(config.comparePeriod)} />
                  ) : null}
                </button>
              );
            })}
          </dl>
        </div>
      </section>

      <div className="mb-4 grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        {show('attention') && (
          <Panel
            title="Needs a touch"
            actions={
              <Segmented
                value={workKind}
                onChange={setWorkKind}
                options={[
                  { id: 'all', label: `All ${work.length}` },
                  { id: 'overdue', label: `Overdue ${work.filter((r) => r.kind === 'overdue').length}` },
                  { id: 'due', label: `Due ${work.filter((r) => r.kind === 'due').length}` },
                  { id: 'expiring', label: `Expiring ${work.filter((r) => r.kind === 'expiring').length}` },
                  { id: 'idle', label: `Idle ${work.filter((r) => r.kind === 'idle').length}` },
                ]}
              />
            }
          >
            {filteredWork.length === 0 ? (
              <p className="text-sm text-gray-500">
                Nothing in this bucket. Pull from{' '}
                <button type="button" className="font-medium text-gray-800 hover:underline" onClick={() => navigate('/admin/sales/prospects')}>
                  Prospects
                </button>{' '}
                or{' '}
                <button type="button" className="font-medium text-gray-800 hover:underline" onClick={() => navigate('/admin/sales/prospects/search')}>
                  Search
                </button>
                .
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredWork.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => open(row.subjectId)}
                      className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-gray-50"
                    >
                      <span className={cn('w-20 shrink-0 rounded-md px-1.5 py-0.5 text-center text-[11px] font-medium', KIND_CLASS[row.kind])}>
                        {KIND_LABEL[row.kind]}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-900">{row.title}</div>
                        <div className="truncate text-xs text-gray-500">{row.detail}</div>
                      </div>
                      <span className={cn('text-xs font-medium', row.late ? 'text-rose-700' : 'text-gray-400')}>{row.when}</span>
                      <span className="w-20 text-right text-sm tabular-nums text-gray-900">{compactGhs(row.ghs)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {scope === 'me' && extraQueue.length > 0 && (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Also on your book</p>
                <ul className="divide-y divide-gray-100">
                  {extraQueue.map((c) => (
                    <li key={c.id}>
                      <ClaimRow
                        claim={c}
                        now={now}
                        onOpen={open}
                        extra={CLAIM_STATUS_LABEL[displayClaimStatus(c, now)]}
                        subjectName={subjectById(c.subjectId)?.displayName}
                        purposeName={purposeById(c.purposeId)?.name}
                        purposeGhs={purposeById(c.purposeId)?.estimatedValueGhs}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Panel>
        )}

        <div className="space-y-4">
          {show('funnel') && (
            <Panel title="Claim funnel" actions={<span className="text-xs text-gray-400">Share of claims this window</span>}>
              <ul className="space-y-2">
                {insights.funnel.map((s) => (
                  <li key={s.id} className="grid grid-cols-[5.5rem_1fr_auto] items-center gap-2 text-sm">
                    <span className="text-gray-600">{s.label}</span>
                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={cn('h-full rounded-full', s.id === 'lost' ? 'bg-rose-400' : 'bg-slate-800')}
                        style={{ width: `${(s.n / funnelMax) * 100}%` }}
                      />
                    </div>
                    <span className="tabular-nums text-gray-500">
                      {s.n}
                      {s.rate == null ? '' : ` · ${pct(s.rate)}`}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-gray-400">
                Live book{' '}
                {insights.liveBook
                  .map((s) => `${s.n} ${s.label.toLowerCase()}`)
                  .join(' · ')}
              </p>
            </Panel>
          )}

          {show('desks') && (
            <Panel title="Desks" actions={<span className="text-xs text-gray-400">Click to filter</span>}>
              <ul className="space-y-2">
                {insights.desks.map((cat) => {
                  const max = Math.max(1, ...insights.desks.map((d) => d.ghs));
                  const on = vertical === cat.id;
                  return (
                    <li key={cat.id}>
                      <button
                        type="button"
                        onClick={() => setVertical(on ? 'all' : (cat.id as SalesVertical))}
                        className={cn('grid w-full grid-cols-[5.5rem_1fr_auto] items-center gap-2 rounded-md px-1 py-0.5 text-left text-sm', on && 'bg-gray-50')}
                      >
                        <span className="truncate font-medium text-gray-800">{cat.label}</span>
                        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                          <div className="h-full rounded-full bg-slate-800" style={{ width: `${(cat.ghs / max) * 100}%` }} />
                        </div>
                        <span className="tabular-nums text-gray-500">
                          {compactGhs(cat.ghs)} · {cat.n}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          )}

          {show('acquisition') && (
            <div className="grid grid-cols-4 gap-2">
              {insights.acquisition.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => navigate(s.id === 'research' ? '/admin/sales/prospects/search' : '/admin/sales/prospects')}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-left hover:border-gray-300"
                >
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{s.label}</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{s.n}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {show('agents') && scope !== 'me' && (
        <Panel title="Agents" className="mb-4" actions={<span className="text-xs text-gray-400">Click a row to view as them</span>}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="pb-2 pr-3 font-medium">Agent</th>
                  <th className="pb-2 pr-3 font-medium">Level</th>
                  <th className="pb-2 pr-3 font-medium">Quota</th>
                  <th className="pb-2 pr-3 font-medium">Won</th>
                  <th className="pb-2 pr-3 font-medium">In play</th>
                  <th className="pb-2 pr-3 font-medium">SLA</th>
                  <th className="pb-2 pr-3 font-medium">Outreach</th>
                  <th className="pb-2 font-medium">Book</th>
                </tr>
              </thead>
              <tbody>
                {agentRows.map((a) => (
                  <tr
                    key={a.id}
                    className={cn(
                      'border-t border-gray-100',
                      (canActAsAnyone || a.id === rep?.id) && 'cursor-pointer hover:bg-gray-50',
                      rep?.id === a.id && 'bg-gray-50',
                    )}
                    onClick={() => {
                      if (canActAsAnyone) setCurrentRep(a.id);
                      else if (a.id !== rep?.id) return;
                      setScope('me');
                    }}
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <span className={cn('flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold text-white', a.avatarColor)}>
                          {initials(a.name)}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">
                            {a.name}
                            {rep && a.id === rep.id ? <span className="ml-1.5 text-xs font-normal text-gray-400">you</span> : null}
                          </div>
                          {a.overdue > 0 && <div className="text-[11px] text-rose-700">{a.overdue} overdue</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-gray-500">
                      {SALES_REP_ROLE_LABEL[a.role]} · {a.title}
                    </td>
                    <td className="min-w-[9rem] py-3 pr-3">
                      {a.quotaGhs ? (
                        <div>
                          <div className="mb-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className={cn(
                                'h-full rounded-full',
                                a.attainment >= 1 ? 'bg-emerald-600' : a.attainment >= 0.5 ? 'bg-slate-800' : 'bg-amber-500',
                              )}
                              style={{ width: `${Math.min(100, a.attainment * 100)}%` }}
                            />
                          </div>
                          <span className="text-xs tabular-nums text-gray-500">{pct(a.attainment)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 tabular-nums text-gray-900">{compactGhs(a.wonGhs)}</td>
                    <td className="py-3 pr-3 tabular-nums text-gray-900">{compactGhs(a.pipelineGhs)}</td>
                    <td className={cn('py-3 pr-3 tabular-nums', a.slaHitRate < 0.5 ? 'text-rose-700' : 'text-gray-600')}>{pct(a.slaHitRate)}</td>
                    <td className="py-3 pr-3 tabular-nums text-gray-600">
                      {a.outreachSent} · {pct(a.replyRate)}
                    </td>
                    <td className="py-3 tabular-nums text-gray-600">
                      {a.active}/{policy.maxConcurrentPerRep}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {show('trend') && (
        <Panel title="Won value" className="mb-4" actions={<span className="text-xs text-gray-400">Closed GHS in this window</span>}>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={insights.series} margin={{ left: 8, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="wonGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={EMERALD} stopOpacity={0.28} />
                    <stop offset="95%" stopColor={EMERALD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  width={44}
                  tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="wonGhs" name="Won GHS" stroke={INK} fill="url(#wonGrad)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      {show('breakdown') && (
        <Panel
          title="Breakdown"
          className="mb-4"
          actions={
            <select className={selectCls} value={config.groupBy} onChange={(e) => patch({ groupBy: e.target.value as DashGroupBy })}>
              <option value="vertical">Desk</option>
              <option value="goal">Goal</option>
              <option value="source">Source</option>
              <option value="kind">Kind</option>
            </select>
          }
        >
          <ul className="space-y-2">
            {breakdown.map((row) => {
              const max = Math.max(1, ...breakdown.map((x) => x.ghs || x.n));
              return (
                <li key={row.id} className="grid grid-cols-[7rem_1fr_auto] items-center gap-2 text-sm">
                  <span className="truncate text-gray-600">{row.label}</span>
                  <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-slate-800" style={{ width: `${((row.ghs || row.n) / max) * 100}%` }} />
                  </div>
                  <span className="tabular-nums text-gray-500">
                    {row.n} · {compactGhs(row.ghs)}
                  </span>
                </li>
              );
            })}
          </ul>
        </Panel>
      )}

      {(show('outreach') || show('loss')) && (
        <div className="mb-4 grid gap-4 lg:grid-cols-2">
          {show('outreach') && (
            <Panel title="Outreach by channel">
              <ul className="space-y-2">
                {insights.byChannel.map((ch) => {
                  const max = Math.max(1, ...insights.byChannel.map((x) => x.sent));
                  return (
                    <li key={ch.id} className="grid grid-cols-[6.5rem_1fr_auto] items-center gap-2 text-sm">
                      <span className="truncate text-gray-600">{ch.label}</span>
                      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-slate-800" style={{ width: `${(ch.sent / max) * 100}%` }} />
                      </div>
                      <span className="tabular-nums text-gray-500">
                        {ch.sent} sent · {ch.replied} replied
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          )}
          {show('loss') && (
            <Panel title="Loss & feedback">
              <ul className="space-y-2">
                {insights.lossReasons.map((r) => {
                  const max = Math.max(1, ...insights.lossReasons.map((x) => x.n));
                  return (
                    <li key={r.id} className="grid grid-cols-[7rem_1fr_1.5rem] items-center gap-2 text-sm">
                      <span className="truncate text-gray-600">{r.label}</span>
                      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-rose-400" style={{ width: `${(r.n / max) * 100}%` }} />
                      </div>
                      <span className="text-right tabular-nums text-gray-500">{r.n}</span>
                    </li>
                  );
                })}
              </ul>
              <ul className="mt-4 flex flex-wrap gap-1.5">
                {insights.byOutcome
                  .filter((o) => o.n > 0)
                  .map((o) => (
                    <li key={o.id} className="rounded-full bg-gray-50 px-2.5 py-1 text-xs text-gray-600">
                      {o.label} · {o.n}
                    </li>
                  ))}
              </ul>
            </Panel>
          )}
        </div>
      )}

      {show('aging') && (
        <div className="mb-4 grid gap-4 lg:grid-cols-3">
          <Panel title="Overdue" actions={<span className="text-xs text-rose-700">{insights.overdue.length}</span>}>
            {insights.overdue.length === 0 ? (
              <p className="text-sm text-gray-400">None overdue.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {insights.overdue.slice(0, 6).map((c) => (
                  <li key={c.id}>
                    <ClaimRow
                      claim={c}
                      now={now}
                      onOpen={open}
                      extra={getRep(c.ownerId)?.name}
                      subjectName={subjectById(c.subjectId)?.displayName}
                      purposeName={purposeById(c.purposeId)?.name}
                      purposeGhs={purposeById(c.purposeId)?.estimatedValueGhs}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Expiring in 48h" actions={<span className="text-xs text-amber-700">{insights.expiringSoon.length}</span>}>
            {insights.expiringSoon.length === 0 ? (
              <p className="text-sm text-gray-400">Nothing expiring soon.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {insights.expiringSoon.slice(0, 6).map((c) => (
                  <li key={c.id}>
                    <ClaimRow
                      claim={c}
                      now={now}
                      onOpen={open}
                      extra={getRep(c.ownerId)?.name}
                      subjectName={subjectById(c.subjectId)?.displayName}
                      purposeName={purposeById(c.purposeId)?.name}
                      purposeGhs={purposeById(c.purposeId)?.estimatedValueGhs}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Idle" actions={<span className="text-xs text-gray-500">{insights.idle.length}</span>}>
            {insights.idle.length === 0 ? (
              <p className="text-sm text-gray-400">No idle claims.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {insights.idle.slice(0, 6).map((c) => (
                  <li key={c.id}>
                    <ClaimRow
                      claim={c}
                      now={now}
                      onOpen={open}
                      extra={getRep(c.ownerId)?.name}
                      subjectName={subjectById(c.subjectId)?.displayName}
                      purposeName={purposeById(c.purposeId)?.name}
                      purposeGhs={purposeById(c.purposeId)?.estimatedValueGhs}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}

      {show('offers') && (
        <Panel title="Offers" className="mb-4" actions={<span className="text-xs text-gray-400">{formatGhs(insights.offerCostGhs)} spent</span>}>
          {insights.offers.every((o) => o.n === 0) ? (
            <p className="text-sm text-gray-400">No offers closed in this window.</p>
          ) : (
            <ul className="space-y-2">
              {insights.offers
                .filter((o) => o.n > 0)
                .map((o) => (
                  <li key={o.id} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-gray-700">
                      {o.name} <span className="text-gray-400">×{o.n}</span>
                    </span>
                    <span className="tabular-nums text-gray-900">
                      {compactGhs(o.wonGhs)} <span className="text-gray-400">· cost {compactGhs(o.costGhs)}</span>
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </Panel>
      )}

      {show('activity') && (
        <Panel title="Recent wins" className="mb-4">
          {insights.recentWins.length === 0 ? (
            <p className="text-sm text-gray-400">No wins in this window.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {insights.recentWins.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => open(c.subjectId)}
                    className="flex w-full items-center gap-3 py-2 text-left hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900">{subjectById(c.subjectId)?.displayName}</div>
                      <div className="truncate text-xs text-gray-500">
                        {getRep(c.ownerId)?.name} · {c.win?.at ? new Date(c.win.at).toLocaleDateString('en-GB') : ''}
                      </div>
                    </div>
                    <span className="text-sm tabular-nums text-gray-900">{compactGhs(purposeById(c.purposeId)?.estimatedValueGhs ?? 0)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}
    </SalesPageShell>
  );
}
